import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, BadgeCheck, Package, ClipboardCheck, ChevronDown } from "lucide-react";
import SEO from "../components/SEO";
import Marquee from "../components/Marquee";
import StatBand from "../components/StatBand";
import CTABanner from "../components/CTABanner";
import { Reveal, MaskedLines } from "../components/motion";
import { SERVICES, PROCESS, TESTIMONIALS, IMG, COMPANY } from "../lib/site";

const Star = ({ className = "h-4 w-4" }) => (
  <svg viewBox="0 0 20 20" className={className} fill="currentColor" aria-hidden="true">
    <path d="M10 1.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L10 14.9l-5.2 2.7 1-5.8L1.5 7.7l5.9-.9L10 1.5z" />
  </svg>
);

const HERO_LINES = [
  <span key="1">The Home</span>,
  <span key="2">Improvement</span>,
  <em key="3" className="italic text-gold">Experts.</em>,
];

const Home = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <>
      <SEO
        title="Esskay Homes | Home Improvement & Construction Experts"
        description="Premium loft conversions, kitchens, bathrooms, extensions, refurbishments and new builds across London & the South East. Fully insured, all-inclusive, 12-month warranty."
        path="/"
      />

      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-[100svh] items-end overflow-hidden" data-testid="home-hero">
        <motion.div style={{ y: imgY }} className="absolute inset-0">
          <motion.img
            src={IMG.hero}
            alt="Contemporary glass extension built by Esskay Homes"
            initial={{ scale: 1.14 }}
            animate={{ scale: 1 }}
            transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
            className="h-full w-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/60 to-navy/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy/70 via-transparent to-navy/30" />

        <motion.div
          style={{ opacity: contentOpacity }}
          className="container-ek relative pb-24 pt-44 md:pb-32"
        >
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="eyebrow flex items-center gap-3"
          >
            <span className="inline-block h-px w-12 bg-gold" />
            {COMPANY.area}
          </motion.p>

          <h1 className="mt-6 font-serif text-[clamp(3.2rem,9vw,7.5rem)] font-medium leading-[0.98] tracking-tight text-white">
            <MaskedLines animate delay={0.35} lines={HERO_LINES} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-slate-300 md:text-lg"
          >
            Loft conversions, kitchens, bathrooms, extensions and full
            refurbishments — designed, built and project-managed end-to-end by
            one trusted team, all backed by a 12-month warranty.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15 }}
            className="mt-10 flex flex-col gap-4 sm:flex-row"
          >
            <Link to="/contact" data-testid="hero-cta-quote" className="btn-gold group">
              Get a Free Quote
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link to="/services/loft-conversions" data-testid="hero-cta-services" className="btn-ghost">
              View Our Services
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.4 }}
            className="mt-14 flex flex-wrap items-center gap-x-8 gap-y-3 text-[11px] uppercase tracking-[0.22em] text-slate-400"
          >
            {[
              { icon: ShieldCheck, label: "12-Month Warranty" },
              { icon: BadgeCheck, label: "Fully Insured" },
              { icon: Package, label: "All-Inclusive Labour & Materials" },
            ].map((t) => (
              <span key={t.label} className="flex items-center gap-2.5" data-testid={`hero-trust-${t.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <t.icon className="h-4 w-4 text-gold" />
                {t.label}
              </span>
            ))}
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8, duration: 1 }}
          className="absolute bottom-8 right-8 hidden flex-col items-center gap-3 md:flex"
        >
          <span className="text-[10px] uppercase tracking-[0.3em] text-slate-400 [writing-mode:vertical-lr]">
            Scroll
          </span>
          <motion.span
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          >
            <ChevronDown className="h-4 w-4 text-gold" />
          </motion.span>
        </motion.div>
      </section>

      <Marquee />

      {/* INTRO */}
      <section className="container-ek grid items-center gap-14 py-24 lg:grid-cols-2 lg:gap-24 lg:py-32" data-testid="home-intro">
        <div>
          <Reveal>
            <p className="eyebrow flex items-center gap-3">
              <span className="inline-block h-px w-10 bg-gold" />
              Who We Are
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl font-medium leading-[1.08] text-white sm:text-5xl lg:text-[3.4rem]">
              Building homes with the care of a{" "}
              <em className="italic text-gold">craftsman</em> and the precision
              of an <em className="italic text-gold">architect</em>.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 text-base leading-relaxed text-mist md:text-lg">
              Esskay Homes is an established home improvement and construction
              company transforming properties across London and the South East.
              From a single bathroom to a full structural renovation, every
              project is planned, built and managed end-to-end by our own
              trusted team.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/about" data-testid="intro-about-link" className="btn-ghost">
                Our Story
              </Link>
              <Link to="/gallery" data-testid="intro-gallery-link" className="link-underline self-center text-[12px] font-medium uppercase tracking-[0.22em] text-gold">
                View Recent Work
              </Link>
            </div>
          </Reveal>
        </div>

        <div className="relative">
          <Reveal delay={0.15}>
            <div className="corner-frame relative overflow-hidden">
              <img
                src={IMG.living2}
                alt="Refurbished living room with stone fireplace"
                className="aspect-[4/5] w-full object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>
          <Reveal delay={0.35} className="absolute -bottom-10 -left-4 hidden w-52 md:block lg:-left-14">
            <div className="border-4 border-navy shadow-2xl">
              <img
                src={IMG.craft}
                alt="Craftsman at work"
                className="aspect-square w-full object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="bg-navy2/40 py-24 lg:py-32" data-testid="home-services">
        <div className="container-ek">
          <div className="mb-14 flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              <Reveal>
                <p className="eyebrow flex items-center gap-3">
                  <span className="inline-block h-px w-10 bg-gold" />
                  What We Build
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-6 font-serif text-4xl font-medium leading-[1.08] text-white sm:text-5xl">
                  Six disciplines. <em className="italic text-gold">One</em>{" "}
                  accountable team.
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <p className="max-w-sm text-sm leading-relaxed text-mist">
                Every service below is delivered by our own directly employed
                tradesmen — never subcontracted away, never out of our control.
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-6">
            {SERVICES.map((s, i) => {
              const spans = ["lg:col-span-4", "lg:col-span-2", "lg:col-span-2", "lg:col-span-4", "lg:col-span-3", "lg:col-span-3"];
              return (
                <Reveal key={s.slug} delay={0.06 * i} className={`${spans[i]} h-full`}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`service-card-${s.slug}`}
                    onMouseMove={(e) => {
                      const r = e.currentTarget.getBoundingClientRect();
                      e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
                      e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
                    }}
                    className="spotlight corner-frame group relative block h-72 overflow-hidden border border-white/10 bg-navy3 lg:h-[22rem]"
                  >
                    <img
                      src={s.image}
                      alt={`${s.name} by Esskay Homes`}
                      loading={i > 1 ? "lazy" : undefined}
                      className="absolute inset-0 h-full w-full object-cover opacity-55 transition-all duration-700 group-hover:scale-105 group-hover:opacity-70"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
                    <span className="absolute right-6 top-5 font-serif text-xl text-gold/70">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="absolute inset-x-0 bottom-0 p-7">
                      <span className="eyebrow flex items-center gap-2.5 text-[10px]">
                        <s.icon className="h-3.5 w-3.5" />
                        Service
                      </span>
                      <div className="mt-3 flex items-center justify-between gap-4">
                        <h3 className="font-serif text-2xl font-medium text-white md:text-[1.7rem]">
                          {s.name}
                        </h3>
                        <span className="flex h-10 w-10 shrink-0 items-center justify-center border border-white/20 transition-all duration-300 group-hover:border-gold group-hover:bg-gold">
                          <ArrowRight className="h-4 w-4 text-white transition-colors duration-300 group-hover:text-navy" />
                        </span>
                      </div>
                      <p className="mt-2 max-w-md text-[13px] leading-relaxed text-slate-400">
                        {s.short}
                      </p>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <StatBand />

      {/* PROCESS */}
      <section className="container-ek py-24 lg:py-32" data-testid="home-process">
        <div className="grid gap-14 lg:grid-cols-[1fr_1.6fr] lg:gap-24">
          <div className="lg:sticky lg:top-32 lg:self-start">
            <Reveal>
              <p className="eyebrow flex items-center gap-3">
                <span className="inline-block h-px w-10 bg-gold" />
                How We Work
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif text-4xl font-medium leading-[1.08] text-white sm:text-5xl">
                From first survey to final <em className="italic text-gold">handover</em>.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-7 max-w-md text-base leading-relaxed text-mist">
                One clear process, one point of contact, zero surprises. You'll
                always know what's happening, when, and exactly what it costs.
              </p>
            </Reveal>
          </div>

          <div>
            {PROCESS.map((p, i) => (
              <Reveal key={p.num} delay={0.08 * i}>
                <div
                  data-testid={`process-step-${i}`}
                  className={`group flex gap-8 py-9 md:gap-12 ${
                    i < PROCESS.length - 1 ? "border-b border-white/10" : ""
                  }`}
                >
                  <span className="font-serif text-4xl font-medium text-gold/50 transition-colors duration-500 group-hover:text-gold md:text-5xl">
                    {p.num}
                  </span>
                  <div>
                    <h3 className="font-serif text-2xl font-medium text-white md:text-3xl">
                      {p.title}
                    </h3>
                    <p className="mt-3 max-w-lg text-sm leading-relaxed text-mist md:text-base">
                      {p.desc}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY PREVIEW */}
      <section className="py-8 lg:py-14" data-testid="home-gallery-preview">
        <div className="container-ek">
          <div className="mb-12 flex items-end justify-between gap-6">
            <Reveal>
              <h2 className="font-serif text-4xl font-medium leading-[1.08] text-white sm:text-5xl">
                Recent <em className="italic text-gold">work</em>.
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <Link
                to="/gallery"
                data-testid="gallery-preview-link"
                className="link-underline hidden text-[12px] font-medium uppercase tracking-[0.22em] text-gold sm:inline-flex"
              >
                View Full Gallery
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
            {[
              { img: IMG.kitchens3, alt: "Shaker kitchen" },
              { img: IMG.living1, alt: "Open-plan living space" },
              { img: IMG.bathrooms3, alt: "En-suite bathroom" },
              { img: IMG.extensions, alt: "Rear extension" },
            ].map((g, i) => (
              <Reveal key={i} delay={0.07 * i}>
                <div className="corner-frame group overflow-hidden" data-testid={`gallery-preview-${i}`}>
                  <img
                    src={g.img}
                    alt={g.alt}
                    loading="lazy"
                    className="aspect-[3/4] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-8 sm:hidden">
            <Link to="/gallery" data-testid="gallery-preview-link-mobile" className="link-underline text-[12px] font-medium uppercase tracking-[0.22em] text-gold">
              View Full Gallery
            </Link>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="container-ek py-24 lg:py-32" data-testid="home-testimonials">
        <div className="mb-14 text-center">
          <Reveal>
            <p className="eyebrow justify-center flex items-center gap-3">
              <span className="inline-block h-px w-10 bg-gold" />
              Client Feedback
              <span className="inline-block h-px w-10 bg-gold" />
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl font-medium leading-[1.08] text-white sm:text-5xl">
              Rated <em className="italic text-gold">5.0</em> by the people who
              matter.
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <div className="mt-5 flex items-center justify-center gap-2" data-testid="google-stars">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-5 w-5 text-gold" />
              ))}
              <span className="ml-3 text-xs uppercase tracking-[0.2em] text-mist">
                Google Reviews
              </span>
            </div>
            <p className="mt-2 text-[11px] uppercase tracking-[0.18em] text-slate-600">
              Illustrative client feedback — live review feed coming soon
            </p>
          </Reveal>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={i} delay={0.08 * i} className="h-full">
              <figure
                data-testid={`testimonial-${i}`}
                className="flex h-full flex-col border border-white/10 bg-navy2/70 p-8 transition-colors duration-500 hover:border-gold/30"
              >
                <div className="flex gap-1">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} className="h-3.5 w-3.5 text-gold" />
                  ))}
                </div>
                <blockquote className="mt-6 flex-1 font-serif text-xl leading-relaxed text-white/90">
                  “{t.quote}”
                </blockquote>
                <figcaption className="mt-8 border-t border-white/10 pt-5">
                  <p className="text-sm font-semibold text-white">{t.name}</p>
                  <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-gold">
                    {t.project} · {t.location}
                  </p>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      <CTABanner />
    </>
  );
};

export default Home;
